import React, { useState, useMemo } from 'react';
import '../../assets/css/case-home.css';
import type { ComponentContentSchema } from '@vibesites/runtime';
// @ts-ignore
import { useContentToken, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { propertySummaries } from './propertyUtils';

const CORPORATE_ADDRESS_1 = '4200 E. Skelly Dr. Suite 800';
const CORPORATE_CITY_STATE_ZIP = 'Tulsa, OK 74135';
const CORPORATE_PHONE = '(918) 492-1983';
const CORPORATE_PHONE_DIGITS = '9184921983';
const MAPS_URL = 'https://www.google.com/maps/search/?api=1&query=4200+E+Skelly+Dr+Suite+800+Tulsa+OK+74135';
const MAP_EMBED_SRC = 'https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=4200+E+Skelly+Dr+Suite+800+Tulsa+OK+74135&zoom=15';
const HIDDEN_TOKEN_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

type WebhookPayload = Record<string, string>;

function postZapierWebhook(url: string, payload: WebhookPayload) {
  return fetch(url, {
    method: 'POST',
    // Send raw JSON, but let the browser keep the request simple for CORS.
    body: JSON.stringify(payload),
  });
}

function AddressIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
      <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
    </svg>
  );
}

export function CorporateContactForm() {
  const title = useContentToken('corporatecontactform.title');

  const sortedProperties = useMemo(() => {
    return [...propertySummaries]
      .map((p: any) => p.property_name || '')
      .filter(Boolean)
      .sort((a: string, b: string) => a.localeCompare(b));
  }, []);

  const [formData, setFormData] = useState({
    location: '',
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'sent' | 'error'>('idle');

  const contactWebhookUrl = useContentToken('corporatecontactform.webhook_url') as string | undefined;
  const vendorWebhookUrl = useContentToken('corporatecontactform.vendor_webhook_url') as string | undefined;

  const [vendorData, setVendorData] = useState({
    location: '',
    businessName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    message: '',
  });
  const [vendorStatus, setVendorStatus] = useState<'idle' | 'submitting' | 'sent' | 'error'>('idle');

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactWebhookUrl) {
      setFormStatus('error');
      return;
    }
    setFormStatus('submitting');
    try {
      const res = await postZapierWebhook(contactWebhookUrl, {
        formType: 'corporate-contact',
        ...formData,
      });
      setFormStatus(res.ok ? 'sent' : 'error');
    } catch {
      setFormStatus('error');
    }
  };

  const handleVendorChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setVendorData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleVendorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendorWebhookUrl) {
      setVendorStatus('error');
      return;
    }
    setVendorStatus('submitting');
    try {
      const res = await postZapierWebhook(vendorWebhookUrl, {
        formType: 'vendor-inquiry',
        ...vendorData,
      });
      setVendorStatus(res.ok ? 'sent' : 'error');
    } catch {
      setVendorStatus('error');
    }
  };

  return (
    <>
      <div aria-hidden="true" style={HIDDEN_TOKEN_STYLE}>
        <EditableToken
          path="corporatecontactform.webhook_url"
          type="url"
          label="Contact Form Webhook URL"
          defaultValue={contentSchema.tokens['corporatecontactform.webhook_url'].defaultValue}
          as="span"
        >
          {contactWebhookUrl || contentSchema.tokens['corporatecontactform.webhook_url'].defaultValue}
        </EditableToken>
        <EditableToken
          path="corporatecontactform.vendor_webhook_url"
          type="url"
          label="Vendor Form Webhook URL"
          defaultValue={contentSchema.tokens['corporatecontactform.vendor_webhook_url'].defaultValue}
          as="span"
        >
          {vendorWebhookUrl || contentSchema.tokens['corporatecontactform.vendor_webhook_url'].defaultValue}
        </EditableToken>
      </div>

      {/* Hero: breadcrumb + title on left, address/phone on right at wide viewports */}
      <section className="cc-hero">
        <div className="case-container">
          <div className="cc-hero-grid">
            <div className="cc-hero-left">
              <nav aria-label="Breadcrumb">
                <ol className="case-breadcrumb">
                  <li><a href="/">Home</a></li>
                  <li aria-current="page">Contact Us</li>
                </ol>
              </nav>
              <EditableToken
                path="corporatecontactform.title"
                type="text"
                label="Page Title"
                defaultValue={contentSchema.tokens['corporatecontactform.title'].defaultValue}
                as="h1"
                className="cc-hero-title"
              >
                {title}
              </EditableToken>
              <div className="cc-hero-separator" />
            </div>
            <div className="cc-hero-right">
              <div className="cc-info-block">
                <div className="cc-info-label">
                  <span className="cc-info-icon"><AddressIcon /></span>
                  <h3>Address</h3>
                </div>
                <p>{CORPORATE_ADDRESS_1}</p>
                <p>{CORPORATE_CITY_STATE_ZIP}</p>
                <a href={MAPS_URL} target="_blank" rel="noopener noreferrer">Get Directions</a>
              </div>
              <div className="cc-info-block">
                <div className="cc-info-label">
                  <span className="cc-info-icon"><PhoneIcon /></span>
                  <h3>Phone</h3>
                </div>
                <p><a href={`tel:${CORPORATE_PHONE_DIGITS}`}>{CORPORATE_PHONE}</a></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Map / Case Cares */}
      <section className="cc-form-section">
        <div className="case-container">
          <div className="cc-form-grid">
            <div className="cc-form-card">
              <h2>Send us a Message</h2>
              <p className="cc-form-intro">
                Please fill out the form below and our friendly staff will get back to you shortly.
              </p>
              <p className="cc-required-note">"*" indicates required fields</p>

              {formStatus === 'sent' ? (
                <div className="cc-form-success">
                  <p>Thank you for your message! We will get back to you within 12 business hours.</p>
                </div>
              ) : formStatus === 'error' ? (
                <div className="cc-form-success" style={{ borderColor: '#c00', color: '#c00' }}>
                  <p>Something went wrong. Please try again or email <a href="mailto:casecares@caseusa.com">casecares@caseusa.com</a> directly.</p>
                  <button type="button" className="case-btn" style={{ marginTop: '1rem' }} onClick={() => setFormStatus('idle')}>Try Again</button>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit}>
                  <div className="cc-field">
                    <label htmlFor="cc-location">Choose a Location <span className="cc-req">*</span></label>
                    <select id="cc-location" name="location" value={formData.location} onChange={handleFormChange} required>
                      <option value="">Select a location...</option>
                      {sortedProperties.map((name: string) => (
                        <option key={name} value={name}>{name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="cc-field-row cc-field-row-2">
                    <div className="cc-field">
                      <label htmlFor="cc-name">Your name <span className="cc-req">*</span></label>
                      <input type="text" id="cc-name" name="name" value={formData.name} onChange={handleFormChange} required />
                    </div>
                    <div className="cc-field">
                      <label htmlFor="cc-email">Email <span className="cc-req">*</span></label>
                      <input type="email" id="cc-email" name="email" value={formData.email} onChange={handleFormChange} required />
                    </div>
                  </div>
                  <div className="cc-field-row cc-field-row-2">
                    <div className="cc-field">
                      <label htmlFor="cc-phone">Phone <span className="cc-req">*</span></label>
                      <input type="tel" id="cc-phone" name="phone" value={formData.phone} onChange={handleFormChange} required />
                    </div>
                    <div className="cc-field">
                      <label htmlFor="cc-message">Message <span className="cc-req">*</span></label>
                      <textarea id="cc-message" name="message" value={formData.message} onChange={handleFormChange} rows={5} required />
                    </div>
                  </div>
                  <div className="cc-form-footer">
                    <button type="submit" className="case-btn" disabled={formStatus === 'submitting'}>
                      {formStatus === 'submitting' ? 'Sending…' : 'Send Message'}
                    </button>
                    <span className="cc-req-note"><span className="cc-req">*</span> Denotes required field.</span>
                  </div>
                  <p className="cc-privacy-note">
                    To learn more about how we use the information you send us, please see our{' '}
                    <a href="/privacy-policy/">privacy policy</a>.<br />
                    All entries will be responded to within 12 business hours.
                  </p>
                </form>
              )}
            </div>

            <div className="cc-right-col">
              <div className="cc-map-wrapper">
                <iframe
                  src={MAP_EMBED_SRC}
                  title="Map of Case &amp; Associates"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <div className="cc-casecares">
                <RichTextContent
                  path="corporatecontactform.mailing_info"
                  label="Mailing & Case Cares Info"
                  defaultValue={contentSchema.tokens['corporatecontactform.mailing_info'].defaultValue}
                  as="div"
                  className="cc-casecares-content"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Prospective Vendors */}
      <section className="cc-vendors">
        <div className="case-container">
          <h2 className="cc-vendors-heading">Prospective Vendors</h2>
          <RichTextContent
            path="corporatecontactform.vendor_intro"
            label="Vendor Intro Text"
            defaultValue={contentSchema.tokens['corporatecontactform.vendor_intro'].defaultValue}
            as="div"
            className="cc-vendors-intro"
          />

          {vendorStatus === 'sent' ? (
            <div className="cc-form-success">
              <p>Thank you for your interest! We will contact you should we need your services.</p>
            </div>
          ) : vendorStatus === 'error' ? (
            <div className="cc-form-success" style={{ borderColor: '#c00', color: '#c00' }}>
              <p>Something went wrong. Please try again or email <a href="mailto:casecares@caseusa.com">casecares@caseusa.com</a> directly.</p>
              <button type="button" className="case-btn" style={{ marginTop: '1rem' }} onClick={() => setVendorStatus('idle')}>Try Again</button>
            </div>
          ) : (
            <form onSubmit={handleVendorSubmit}>
              <div className="cc-field-row cc-field-row-2">
                <div className="cc-field">
                  <label htmlFor="vd-location">Choose a Location <span className="cc-req-italic">(Required)</span></label>
                  <select id="vd-location" name="location" value={vendorData.location} onChange={handleVendorChange} required>
                    <option value="">Select a location...</option>
                    {sortedProperties.map((name: string) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="cc-field">
                  <label htmlFor="vd-business">Your Business Name <span className="cc-req-italic">(Required)</span></label>
                  <input type="text" id="vd-business" name="businessName" value={vendorData.businessName} onChange={handleVendorChange} required />
                </div>
              </div>
              <div className="cc-field-row cc-field-row-3">
                <div className="cc-field">
                  <label htmlFor="vd-name">Contact Name <span className="cc-req-italic">(Required)</span></label>
                  <input type="text" id="vd-name" name="contactName" value={vendorData.contactName} onChange={handleVendorChange} required />
                </div>
                <div className="cc-field">
                  <label htmlFor="vd-email">Contact Email <span className="cc-req-italic">(Required)</span></label>
                  <input type="email" id="vd-email" name="contactEmail" value={vendorData.contactEmail} onChange={handleVendorChange} required />
                </div>
                <div className="cc-field">
                  <label htmlFor="vd-phone">Contact Phone <span className="cc-req-italic">(Required)</span></label>
                  <input type="tel" id="vd-phone" name="contactPhone" value={vendorData.contactPhone} onChange={handleVendorChange} required />
                </div>
              </div>
              <div className="cc-field" style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="vd-message">
                  Message (include all services your business provides, including the markets you service){' '}
                  <span className="cc-req-italic">(Required)</span>
                </label>
                <textarea id="vd-message" name="message" value={vendorData.message} onChange={handleVendorChange} rows={8} required />
              </div>
              <p className="cc-file-note" style={{ marginBottom: '1.25rem' }}>
                If additional documents are needed, our team will request them after reviewing your submission.
              </p>
              <button type="submit" className="case-btn" disabled={vendorStatus === 'submitting'}>
                {vendorStatus === 'submitting' ? 'Submitting…' : 'Submit'}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CorporateContactForm',
  displayName: 'Corporate Contact Form',
  tokens: {
    'corporatecontactform.title': {
      label: 'Page Title',
      type: 'text',
      defaultValue: 'Contact Case & Associates',
    },
    'corporatecontactform.mailing_info': {
      label: 'Mailing & Case Cares Info',
      type: 'richtext',
      defaultValue: `<p><strong>All written correspondence can be sent to:</strong></p>
<p>Case &amp; Associates Properties, Inc.<br>Attn: Tenant Relations<br>4200 E. Skelly Dr., Ste. 800<br>Tulsa, OK 74135</p>
<p>We pride ourselves on Customer Service. Please submit all compliments, questions, and concerns to our Case Cares team at <strong><a href="mailto:casecares@caseusa.com">casecares@caseusa.com</a></strong>.</p>
<p>All entries submitted to this email or through the Contact Us form will be responded to within 12 business hours. Please include your phone number, email, and property name in all correspondence so we can better assist you. Once you've received a response directly from Case Cares, you will be provided the option to add attachments if applicable.</p>
<p>If you choose to leave a voicemail on our Case Cares Customer Service Line at <strong><a href="tel:9186109369">918-610-9369</a></strong>, please know that all calls will be returned within 3 business days. The volume of received calls and emails will determine the time frame in which we respond. We appreciate your patience and understanding.</p>`,
    },
    'corporatecontactform.vendor_intro': {
      label: 'Vendor Intro Text',
      type: 'richtext',
      defaultValue: '<p>Thank you for your interest in providing your services at one or more of our locations! Please fill out the form below and you\'ll be added to our prospective vendors list.</p><p>We will contact you should we need your services.</p>',
    },
    'corporatecontactform.webhook_url': {
      label: 'Contact Form Webhook URL',
      type: 'url',
      defaultValue: '',
      description: 'Zapier webhook URL for the corporate contact form. Submissions are POSTed as a raw JSON string with formType "corporate-contact".',
    },
    'corporatecontactform.vendor_webhook_url': {
      label: 'Vendor Form Webhook URL',
      type: 'url',
      defaultValue: '',
      description: 'Zapier webhook URL for the vendor inquiry form. Submissions are POSTed as a raw JSON string with formType "vendor-inquiry".',
    },
  },
};

(CorporateContactForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default CorporateContactForm;
